/* eslint-disable react/jsx-no-useless-fragment */
import { LinkActionTypeEnum } from '@doltech/utils/lib/constants';
import { Dropdown, Form } from 'antd';
import * as React from 'react';
import { useSlateStatic } from 'slate-react';

import { getNodes } from '../../../../../slatePlugins/common/queries/getNodes';
import { ELEMENT_HYPER_LINK } from '../defaults';
import { HyperLinkInput, HyperLinkInputProps } from './HyperLinkInput';

export interface HyperLinkPopoverProps extends Pick<HyperLinkInputProps, 'onApplyLink'> {
  baseUrl?: string;
  children?: React.ReactNode;
  onPopoverFocus?: () => void;
  onPopoverClose?: () => void;
}

export const HyperLinkPopover = (props: HyperLinkPopoverProps) => {
  const { baseUrl, children, onPopoverClose, onApplyLink, onPopoverFocus } = props;
  const editor = useSlateStatic();

  const [form] = Form.useForm();

  const [isVisible, setVisible] = React.useState(false);
  const [disabled, setDisabled] = React.useState(false);

  const handlePopoverClose = React.useCallback(() => {
    setVisible(false);
    onPopoverClose?.();
  }, [onPopoverClose]);

  const handleApplyLink = React.useCallback(
    (url, linkSettings, linkAction) => {
      if (!url) return;
      setVisible(false);
      onApplyLink?.(url, linkSettings, linkAction);
    },
    [onApplyLink]
  );

  const handleChildMouseDownAction = React.useCallback(() => {
    const nodes = Array.from(
      getNodes(editor, {
        match: { type: ELEMENT_HYPER_LINK } as any,
      }),
      ([node]) => node
    );

    if (nodes.length > 1) {
      setDisabled(true);
    } else {
      setDisabled(false);
      setVisible((oldVisible) => !oldVisible);
      const [linkNode] = nodes as any;
      form.setFieldsValue({
        url: linkNode?.url,
        linkSettings: linkNode?.linkSettings,
        linkAction: linkNode?.linkAction || LinkActionTypeEnum.AUTO,
      });
    }
  }, [editor, form]);

  const childrenWithProps = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, {
        onMouseDown: (event) => {
          handleChildMouseDownAction();
          child.props.onMouseDown?.(event);
        },
      } as any);
    }
    return child;
  });

  return (
    <Dropdown
      overlay={
        <HyperLinkInput
          form={form}
          baseUrl={baseUrl}
          onMouseDown={onPopoverFocus}
          onClose={handlePopoverClose}
          onApplyLink={handleApplyLink}
          withLinkSetting
        />
      }
      disabled={disabled}
      trigger={['click']}
      open={isVisible}
      placement="bottomLeft"
      onVisibleChange={(visibleFlag) => {
        if (!visibleFlag) {
          handlePopoverClose();
        }
      }}
    >
      <>{childrenWithProps}</>
    </Dropdown>
  );
};
